import './App.css';
import React from 'react';
import { withTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

// Composants
import AppRoutes from './js/Routes/AppRoutes';
import LanguageToggle from './js/Components/Common/LanguageToggle';

// Hooks personnalisés
import { useUser } from './js/hooks/useUser';
import { log } from "./js/Helpers/logger";

// Hook personnalisé pour gérer la logique de l'application
const useAppLogic = () => {
  const { user, logout, initialized } = useUser();
  const navigate = useNavigate();
  
  const navigateTo = (path) => {
    navigate(path);
  };
  
  // Debug logs
  React.useEffect(() => {
    log.ui.log('useAppLogic - User state:', { user, initialized });
  }, [user, initialized]);
  
  return { 
    user, 
    logout, 
    navigateTo, 
    initialized 
  };
};

const App = ({ t, i18n }) => {
  log.ui.log('App - Rendering...');
  const { user, logout, navigateTo, initialized } = useAppLogic();
  
  // Afficher un indicateur de chargement tant que l'application n'est pas initialisée
  if (!initialized) {
    log.ui.log('App - Showing loading spinner (not initialized)');
    return (
      <div className="d-flex flex-column justify-content-center align-items-center" style={{ height: '100vh' }}>
        <div className="spinner-border text-primary mb-3" role="status">
          <span className="visually-hidden">{t('common.loading') || 'Chargement…'}</span>
        </div>
        <div>{t('common.loading') || 'Chargement…'}</div>
      </div>
    );
  }

  log.ui.log('App - Initialized, current user:', user);
  
  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };
  
  const handleLogout = () => {
    log.ui.log('App - Logging out...');
    logout();
    // La redirection sera gérée par le composant AppRoutes
  };
  
  // Si l'utilisateur est en cours de chargement (premier rendu)
  if (user === undefined) {
    log.ui.log('App - User is undefined, showing loading state');
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="App" style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <main style={{ flexGrow: 1 }}>
        <AppRoutes />
      </main>

      {/* Bandeau d'action en jeu */}
      {user?.isAuthenticated && <FooterAction onLogout={handleLogout} navigateTo={navigateTo} t={t} />}

      {/* Mention au cycle de l'Œil dormant — toujours visible */}
      <footer
        style={{
          textAlign: 'center',
          padding: '10px 16px',
          fontFamily: 'var(--font-serif)',
          fontStyle: 'italic',
          fontSize: 11,
          color: 'var(--fg-muted)',
          background: 'var(--ash-1000)',
          borderTop: '1px solid rgba(200,144,42,0.10)',
        }}
      >
        {t('footer.mention')}
      </footer>
    </div>
  );
};

// In-game footer — Chronique shortcut + LanguageToggle + leave-the-chronicle.
// LanguageToggle is the single source of truth: same component on Login.
const FooterAction = ({ onLogout, navigateTo, t }) => (
    <footer
      style={{
        width: '100%',
        background: 'var(--ash-900)',
        borderTop: '1px solid rgba(200,144,42,0.18)',
        padding: '10px 24px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 12,
        flexWrap: 'wrap',
      }}
    >
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          type="button"
          className="okbtn okbtn-ghost okbtn-sm"
          onClick={() => navigateTo('/game')}
        >
          {t('common.home')}
        </button>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <LanguageToggle />
        <button
          type="button"
          className="okbtn okbtn-danger okbtn-sm"
          onClick={onLogout}
        >
          {t('common.logout')}
        </button>
      </div>
    </footer>
);

// Envelopper le composant avec le HOC de traduction
const AppWithTranslation = withTranslation()(App);

export default AppWithTranslation;
